/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import {
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { defaultValue } from './KanbanBoard';
import { ItemType } from '../../registry/Base.type';
import { getName } from '../../registry/Manager';

const KanbanBoardManager: ComponentManagerFn<'KanbanBoard'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'KanbanBoard'>(platform, 'KanbanBoard'),
    type: 'KanbanBoard',
    name: 'Kanban Board',
    defaultValue,
    isChildAllowed: (page, item, childType, { pageId, appUid }) => {
      if (
        !(['Box', 'KanbanItem', 'TriggerOnDrop'] as ItemType[]).includes(
          childType,
        )
      ) {
        return false;
      }
      if (childType === 'Box') {
        const children = platform.getChildrenOfType(
          appUid,
          pageId,
          page,
          item,
          childType,
        );
        if (children.length > 0) return `${getName(childType)} already added`;
      }
      return true;
    },
    defaultChildren: ['KanbanItem', 'KanbanItem', 'KanbanItem', 'Box'],
    props: {
      viewAttribute: {
        label: 'View Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
      } as ViewAttributeChooserProp,
      required: true,
      seqAttribute: {
        label: 'Sequence Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['Integer'],
        preventMultiEdit: true,
        required: true,
        helpText:
          'Provide an attribute to update the sequence of the items in the board internally and store the sequence. It should not be related to actual data.',
      } as ViewAttributeChooserProp,
      itemWidth: {
        label: 'Item Width',
        type: 'numberInput',
        helpText: 'Specify value as -1 to auto fit all the items.',
      },
    },
    canHaveChildren: () => true,
    getDisplayText: (item) => '',
    category: 'Data',
    icon: { iconName: 'money-check' },
    description: `**Overview**<br/>
The Kanban Board is a visual workflow component that organizes records into a series of columns (or "lanes"). It transforms static data into an interactive process board, allowing users to track status, prioritize work, and move items through stages simply by dragging and dropping them.
Unlike a Data Grid which shows everything in a list, the Kanban Board is designed for Process Management—perfect for task trackers, sales pipelines, or hiring workflows where the "state" of an item is its most important attribute.

**1. Data Configuration**<br/>
The board is entirely data-driven. It requires specific attributes from your Data Store to understand how to group and order the cards.
* **View Attribute:** The field that defines the columns. (Example: If you select a "Status" field with values "To Do," "In Progress," and "Done," the board will automatically create three columns). When a user drags a card, this attribute is automatically updated.
* **Sequence Attribute:** The field used to remember the order of cards within a column. (Requirement: Must be an Integer field). This is internal logic used to persist the specific order of cards as users move them up and down.

**2. Card Design (Kanban Item)**<br/>
The Kanban Board acts as the container, but it doesn't decide what the individual cards look like. For that, it uses Kanban Item children.
* **Template System:** You add **Kanban Item** components inside the board to define the "Blueprint" for your cards.
* **Composition:** Inside a Kanban Item, you can place Text, Avatars, Icons, or Status Chips to display relevant details (e.g., Task Title, Assignee, Due Date) for each record.

**3. Layout & Sizing**<br/>
* **Item Width:** Controls the horizontal size of the columns.
    * **Fixed Width:** Enter a number (e.g., 300) to set a strict pixel width for columns.
    * **Auto Fit:** Enter -1 to make columns automatically expand to fill the available screen width. This is ideal for boards with few columns (e.g., 3-4 stages) to maximize space usage.

**4. Interaction & Logic**<br/>
The drag-and-drop nature of the board is its primary feature, but you can hook into these events for deeper logic.
* **TriggerOnDrop:** You can add this trigger as a child component. It fires immediately after a card is dropped into a new column. (Use Case: Send a notification when a task is moved to "Done" or update a timestamp when an item enters "In Progress").

**5. Structure Rules**<br/>
To maintain a valid layout, the component enforces strict parent-child relationships.
* **Allowed Children:** KanbanItem (Card template), Box (Optional header/toolbar), TriggerOnDrop (Logic).`,
  };
};

export default KanbanBoardManager;
